import {
  extremosTampaTraseira,
  LARGURA_QUINA_TRASEIRA_HATCH,
  type PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'
import { CORES_AUTOMOTIVAS } from '../../dominio/didatico/tokens'
import { TransformacaoPeca } from './contextoDeformacao'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

interface Props {
  perfil: PerfilVeiculo
  aceso?: boolean
}

/**
 * Conjunto óptico traseiro:
 * - No hatch: lanternas na quina (~20 cm) e dobrando pela lateral (~22 cm).
 * - No sedan: parte fixa na carroceria na lateral (~20 cm) dobrando o canto. A parte traseira da saia
 *   é exportada por `LanternasTampaSedan` e montada dentro da tampa móvel.
 */
export function LanternasVeiculo({ perfil, aceso = true }: Props) {
  const hatch = perfil.volumeTraseiro === 'hatch'

  if (hatch) {
    const alturaLanterna = 0.08
    const yTopo = perfil.cinturaY - 0.04
    const y = yTopo - alturaLanterna / 2
    // z da quina na altura y acompanhando a inclinação da tampa do hatch
    const { de, para } = extremosTampaTraseira(perfil)
    const tInclinacao = (y - de.y) / (para.y - de.y)
    const dzInclinacao = de.z + tInclinacao * (para.z - de.z)
    const zTraseiro = dzInclinacao - 0.015
    const compLateral = 0.22
    const largTraseira = LARGURA_QUINA_TRASEIRA_HATCH
    const xCentroTraseira = perfil.largura / 2 - largTraseira / 2
    const xLateral = perfil.largura / 2 - 0.012

    return (
      <group>
        {[-1, 1].map((sinal) => (
          <TransformacaoPeca
            key={sinal}
            pecaId={`lanterna${sinal > 0 ? 'Esquerda' : 'Direita'}`}
            ancora={[sinal * xCentroTraseira, y, zTraseiro]}
          >
            <group position={[0, 0, 0]}>
              {/* Parte traseira sobre a quina */}
              <group position={[sinal * xCentroTraseira, y, zTraseiro]}>
                {/* Carcaça traseira */}
                <mesh position={[0, 0, 0.01]}>
                  <CaixaArredondada args={[largTraseira, alturaLanterna, 0.03]} raio={0.008} />
                  <meshStandardMaterial color="#0f172a" roughness={0.8} />
                </mesh>
                {/* Guia LED rubi */}
                <mesh position={[-sinal * 0.01, alturaLanterna * 0.12, -0.01]}>
                  <CaixaArredondada args={[largTraseira * 0.88, alturaLanterna * 0.38, 0.015]} raio={0.005} />
                  <meshStandardMaterial
                    color={CORES_AUTOMOTIVAS.lanternaLed}
                    emissive={CORES_AUTOMOTIVAS.lanternaEmissivo}
                    emissiveIntensity={aceso ? 0.85 : 0.25}
                    roughness={0.2}
                  />
                </mesh>
                {/* Luz de ré branca */}
                <mesh position={[sinal * 0.02, -alturaLanterna * 0.22, -0.01]}>
                  <CaixaArredondada args={[largTraseira * 0.45, alturaLanterna * 0.25, 0.012]} raio={0.004} />
                  <meshStandardMaterial
                    color={CORES_AUTOMOTIVAS.lanternaRe}
                    emissive="#f1f5f9"
                    emissiveIntensity={aceso ? 0.4 : 0.05}
                    roughness={0.2}
                  />
                </mesh>
                {/* Lente translúcida */}
                <mesh position={[0, 0, -0.015]}>
                  <CaixaArredondada args={[largTraseira + 0.004, alturaLanterna + 0.004, 0.01]} raio={0.006} />
                  <meshStandardMaterial
                    color={CORES_AUTOMOTIVAS.lanternaLente}
                    emissive={CORES_AUTOMOTIVAS.lanternaEmissivo}
                    emissiveIntensity={0.2}
                    roughness={0.12}
                    metalness={0.1}
                    transparent
                    opacity={0.65}
                    depthWrite={false}
                  />
                </mesh>
              </group>

              {/* Parte que dobra pela lateral */}
              <group position={[sinal * xLateral, y, zTraseiro + compLateral / 2 + 0.01]}>
                <mesh position={[sinal * 0.008, 0, 0]}>
                  <CaixaArredondada args={[0.025, alturaLanterna, compLateral]} raio={0.008} />
                  <meshStandardMaterial color="#0f172a" roughness={0.8} />
                </mesh>
                <mesh position={[sinal * 0.018, alturaLanterna * 0.12, 0]}>
                  <CaixaArredondada args={[0.012, alturaLanterna * 0.38, compLateral * 0.88]} raio={0.005} />
                  <meshStandardMaterial
                    color={CORES_AUTOMOTIVAS.lanternaLed}
                    emissive={CORES_AUTOMOTIVAS.lanternaEmissivo}
                    emissiveIntensity={aceso ? 0.85 : 0.25}
                    roughness={0.2}
                  />
                </mesh>
                <mesh position={[sinal * 0.022, 0, 0]}>
                  <CaixaArredondada args={[0.008, alturaLanterna + 0.004, compLateral + 0.004]} raio={0.006} />
                  <meshStandardMaterial
                    color={CORES_AUTOMOTIVAS.lanternaLente}
                    emissive={CORES_AUTOMOTIVAS.lanternaEmissivo}
                    emissiveIntensity={0.2}
                    roughness={0.12}
                    metalness={0.1}
                    transparent
                    opacity={0.65}
                    depthWrite={false}
                  />
                </mesh>
              </group>
            </group>
          </TransformacaoPeca>
        ))}
      </group>
    )
  }

  // Sedan: parte fixa na carroceria, correndo pela lateral e dobrando o canto
  const alturaLanterna = 0.085
  const y = perfil.cinturaY - 0.03
  const zCanto = perfil.traseiraZ + 0.05
  const compLateral = 0.20
  const zLateral = zCanto + compLateral / 2
  const xLateral = perfil.largura / 2 - 0.012
  const xCanto = perfil.largura / 2 - 0.025

  return (
    <group>
      {[-1, 1].map((sinal) => (
        <TransformacaoPeca
          key={sinal}
          pecaId={`lanterna${sinal > 0 ? 'Esquerda' : 'Direita'}`}
          ancora={[sinal * xLateral, y, zLateral]}
        >
          <group position={[0, 0, 0]}>
            {/* Faixa correndo pela lateral */}
            <group position={[sinal * xLateral, y, zLateral]}>
              <mesh position={[sinal * 0.008, 0, 0]}>
                <CaixaArredondada args={[0.025, alturaLanterna, compLateral]} raio={0.008} />
                <meshStandardMaterial color="#0f172a" roughness={0.8} />
              </mesh>
              <mesh position={[sinal * 0.018, alturaLanterna * 0.12, 0]}>
                <CaixaArredondada args={[0.012, alturaLanterna * 0.4, compLateral * 0.85]} raio={0.005} />
                <meshStandardMaterial
                  color={CORES_AUTOMOTIVAS.lanternaLed}
                  emissive={CORES_AUTOMOTIVAS.lanternaEmissivo}
                  emissiveIntensity={aceso ? 0.85 : 0.25}
                  roughness={0.2}
                />
              </mesh>
              <mesh position={[sinal * 0.022, 0, 0]}>
                <CaixaArredondada args={[0.008, alturaLanterna + 0.004, compLateral + 0.004]} raio={0.006} />
                <meshStandardMaterial
                  color={CORES_AUTOMOTIVAS.lanternaLente}
                  emissive={CORES_AUTOMOTIVAS.lanternaEmissivo}
                  emissiveIntensity={0.2}
                  roughness={0.12}
                  metalness={0.1}
                  transparent
                  opacity={0.65}
                  depthWrite={false}
                />
              </mesh>
            </group>

            {/* Curva no canto traseiro */}
            <group position={[sinal * xCanto, y, zCanto - 0.01]}>
              <mesh>
                <CaixaArredondada args={[0.045, alturaLanterna, 0.025]} raio={0.008} />
                <meshStandardMaterial color="#0f172a" roughness={0.8} />
              </mesh>
              <mesh position={[0, alturaLanterna * 0.12, -0.008]}>
                <CaixaArredondada args={[0.04, alturaLanterna * 0.4, 0.015]} raio={0.005} />
                <meshStandardMaterial
                  color={CORES_AUTOMOTIVAS.lanternaLed}
                  emissive={CORES_AUTOMOTIVAS.lanternaEmissivo}
                  emissiveIntensity={aceso ? 0.85 : 0.25}
                  roughness={0.2}
                />
              </mesh>
              <mesh position={[0, 0, -0.014]}>
                <CaixaArredondada args={[0.046, alturaLanterna + 0.004, 0.01]} raio={0.006} />
                <meshStandardMaterial
                  color={CORES_AUTOMOTIVAS.lanternaLente}
                  emissive={CORES_AUTOMOTIVAS.lanternaEmissivo}
                  emissiveIntensity={0.2}
                  roughness={0.12}
                  metalness={0.1}
                  transparent
                  opacity={0.65}
                  depthWrite={false}
                />
              </mesh>
            </group>
          </group>
        </TransformacaoPeca>
      ))}
    </group>
  )
}

/**
 * Parte traseira da lanterna do sedan, montada sobre a saia da tampa traseira.
 * Fica como filha da tampa e levanta junto com ela ao abrir.
 */
export function LanternasTampaSedan({ perfil, aceso = true }: Props) {
  if (perfil.volumeTraseiro !== 'sedan') return null

  const alturaLanterna = 0.085
  const y = perfil.cinturaY - 0.03
  const z = perfil.traseiraZ + 0.05 - 0.02
  const largTraseira = 0.28
  // Estende da borda da tampa (perfil.largura / 2 - 0.02) para dentro
  const xCentro = perfil.largura / 2 - 0.02 - largTraseira / 2

  return (
    <group name="lanternasTampaSedan">
      {[-1, 1].map((sinal) => (
        <group key={sinal} position={[sinal * xCentro, y, z]}>
          {/* Carcaça traseira */}
          <mesh position={[0, 0, 0.008]}>
            <CaixaArredondada args={[largTraseira, alturaLanterna, 0.025]} raio={0.008} />
            <meshStandardMaterial color="#0f172a" roughness={0.8} />
          </mesh>
          {/* Guia LED rubi */}
          <mesh position={[-sinal * 0.02, alturaLanterna * 0.12, -0.008]}>
            <CaixaArredondada args={[largTraseira * 0.88, alturaLanterna * 0.4, 0.015]} raio={0.005} />
            <meshStandardMaterial
              color={CORES_AUTOMOTIVAS.lanternaLed}
              emissive={CORES_AUTOMOTIVAS.lanternaEmissivo}
              emissiveIntensity={aceso ? 0.85 : 0.25}
              roughness={0.2}
            />
          </mesh>
          {/* Luz de ré branca */}
          <mesh position={[sinal * 0.04, -alturaLanterna * 0.22, -0.008]}>
            <CaixaArredondada args={[largTraseira * 0.35, alturaLanterna * 0.25, 0.012]} raio={0.004} />
            <meshStandardMaterial
              color={CORES_AUTOMOTIVAS.lanternaRe}
              emissive="#f1f5f9"
              emissiveIntensity={aceso ? 0.4 : 0.05}
              roughness={0.2}
            />
          </mesh>
          {/* Lente translúcida */}
          <mesh position={[0, 0, -0.014]}>
            <CaixaArredondada args={[largTraseira + 0.004, alturaLanterna + 0.004, 0.01]} raio={0.006} />
            <meshStandardMaterial
              color={CORES_AUTOMOTIVAS.lanternaLente}
              emissive={CORES_AUTOMOTIVAS.lanternaEmissivo}
              emissiveIntensity={0.2}
              roughness={0.12}
              metalness={0.1}
              transparent
              opacity={0.65}
              depthWrite={false}
            />
          </mesh>
        </group>
      ))}
    </group>
  )
}
